import { useEffect, useRef, useState } from 'react'
import { useJobsFeed } from 'hooks/use-jobs-feed/useJobsFeed'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { IJobProps, JobProps } from 'common/types/job.types'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { debounce } from 'common/utils/debounce'
import { cities, provinces } from 'common/select-options'
import { useProfilePercentage } from 'hooks/use-profile-percentage/useProfilePercentage'
import { CountryNames } from 'pages/content/components/jobs/components/template/JobFormMobile/types'
import { useLocation } from 'react-router-dom'

export const useJobsPage = () => {
  const getPage = () => {
    const params = new URL(document.location.toString()).searchParams
    let _page = parseInt(params.get('page'))

    return _page || 1
  }

  const { backendUser, backendPartner } = useAuth()
  const { getProfilePercentage } = useProfilePercentage()
  const [page, setPage] = useState(getPage() || 1)
  const [jobDrawer, setJobDrawer] = useState(false)
  const { jobs, lastResponse, filter, setFilter, fetchJobs, noOfSearchResults, fetchingJobs } = useJobsFeed(6)
  const [showJobLimitModal, setShowJobLimitModal] = useState<boolean>(false)
  const [showFiltersCaret, setShowFiltersCaret] = useState(true)
  const [selectedJob, setSelectedJob] = useState<JobProps | null>(null)
  const [firstLoaded, setFirstLoaded] = useState(false)
  const [datePostedDrawerOpen, setDatePostedDrawerOpen] = useState(false)
  const [jobTypeDrawerOpen, setJobTypeDrawerOpen] = useState(false)
  const [workplaceDrawerOpen, setWorkplaceDrawerOpen] = useState(false)
  const [countryDrawerOpen, setCountryDrawerOpen] = useState(false)
  const [provinceDrawerOpen, setProvinceDrawerOpen] = useState(false)
  const [cityDrawerOpen, setCityDrawerOpen] = useState(false)
  const { logEventAnalytics } = useAnalytics()
  const [searchCriteria, setSearchCriteria] = useState('')
  const { isDevice } = useScreenSizes()
  const [location, setLocation] = useState(['', '', ''])
  const [isCanadaOrUS, setIsCanadaOrUS] = useState('')
  const { search } = useLocation()

  useEffect(() => {
      setPage(getPage())
  }, [search])

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  const hasFilters = !!Object.keys(filter)?.length || false
  const hasJobs = !!jobs?.length || false

  useEffect(() => {
    if (!isDevice) {
      if (jobs?.length) {
        setSelectedJob(jobs?.[0])
        setFirstLoaded(true)
        document.getElementById(`job-${jobs?.[0]?.id}`)?.scrollIntoView()
        document.getElementById(`jobs-search-container`)?.scrollIntoView()
      } else {
        setSelectedJob(null)
      }
    } else {
      setFirstLoaded(true)
    }
  }, [jobs])

  useEffect(() => {
    isDevice && document.getElementById('jobs-search-container')?.scrollIntoView?.()
    
    fetchJobs(page)
  }, [page])

  useEffect(() => {
    const updateFilter = debounce(() => {
      setFilter(prev => {
        const payload = {
          ...prev,
          titleAndCompany: searchCriteria.trim()
        }
        return payload
      })
    })
    updateFilter()
  }, [searchCriteria])

  useEffect(() => {
    if (firstLoaded || isDevice) {
      fetchJobs(page)
    }
  }, [filter])

  const handleJobClick = (jobId: string) => {
    const job = jobs?.find(job => job.id === jobId)

    if (!isDevice) {
      setSelectedJob(job as JobProps)
    }

    logEventAnalytics(
      'Partner',
      'View particular job',
      'mixpanel',
      'View particular job',
      {
        'User ID': backendUser?.id,
        'Email address': backendUser?.username,
        'Partner ID': backendPartner?.id,
        'Company Name': backendPartner?.name,
        'Account Type': 'Partner',
        'Job Company': job?.companyName,
        'Job ID': job?.id,
        'Job Name': job?.title
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const trackJobSearchEvent = () => {
    const partnerLog = {}
    const userLog = {}

    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendUser?.partnerId
      partnerLog['Company Name'] = backendPartner?.name
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Job search made',
      'mixpanel',
      'Job search made',
      {
        'User ID': backendUser?.id,
        'Profile search query': searchCriteria,
        'Profile search results': noOfSearchResults,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
        ...userLog,
        ...partnerLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const onValueChange = (e: any) => {
    //@ts-ignore
    let { name, value } = {name: e.target.name, value: e.target.value}

    if (name === 'country' && value) {
      setLocation([value, '', ''])
      if (value === CountryNames.CA) {
        setIsCanadaOrUS('Canada')
        onValueChange({ target: { name: IJobProps.PROVINCES, value: '' }})
      } else if (value === CountryNames.US) {
        setIsCanadaOrUS('United States')
        onValueChange({ target: { name: IJobProps.PROVINCES, value: '' }})
      } else {
        setIsCanadaOrUS('Outside North America')
      }
    }

    if (name === 'province' && value) {
      setLocation([location?.[0], value, ''])
    }

    if (name === 'city' && value) {
      setLocation([location?.[0], location?.[1], value])
    }

    setFilter((preValues) => ({
      ...preValues,
      [name]: value,
    }))

  }

  const provinceList = provinces?.find(countryData => countryData.country == isCanadaOrUS)?.states?.map(val => {
    return ({
      ...val,
      value: val?.label
    })
  }) || []

  const cityList: any = cities?.find(stateData => stateData.country == isCanadaOrUS && stateData.state == location?.[1])?.cities.map(val => {
    return ({
      ...val,
      value: val?.label
    })
  }) || []

  return {
    jobs,
    lastResponse,
    filter,
    setFilter,
    jobDrawer,
    setJobDrawer,
    page,
    setPage,
    selectedJob,
    setSelectedJob,
    handleJobClick,
    handlePageChange,
    hasFilters,
    hasJobs,
    backendUser,
    backendPartner,
    showJobLimitModal,
    setShowJobLimitModal,
    setSearchCriteria,
    searchCriteria,
    trackJobSearchEvent,
    fetchingJobs,
    showFiltersCaret,
    setShowFiltersCaret,
    datePostedDrawerOpen,
    setDatePostedDrawerOpen,
    jobTypeDrawerOpen,
    setJobTypeDrawerOpen,
    workplaceDrawerOpen,
    setWorkplaceDrawerOpen,
    countryDrawerOpen,
    setCountryDrawerOpen,
    onValueChange,
    location,
    isCanadaOrUS,
    setIsCanadaOrUS,
    provinceList,
    cityList,
    provinceDrawerOpen,
    setProvinceDrawerOpen,
    cityDrawerOpen,
    setCityDrawerOpen,
    getProfilePercentage
  } as const
}
